<template>
  <Card
    :pt="{
      body: {
        class: 'px-4 py-3 line-height-3',
      },
    }"
  >
    <template #content>
      <div class="grid">
        <div class="col-3 py-0">
          <span class="text-sm text-gray-400">
            {{ compiledCampaign.org_name || '-' }}
          </span><br />
          <span class="text-lg font-bold">
            <router-link
              :to="{
                name: ROUTE_ADVERTISER_CAMPAIGN_DETAILS,
                params: {
                  campaignId: compiledCampaign.id,
                }
              }"
            >
              <Button
                class="p-0 text-left"
                link
                :label="compiledCampaign.campaign_name || '-'"
              />
            </router-link>
          </span>
        </div>

        <div class="col-2 py-0">
          <span class="text-sm text-gray-400">
            Ends {{ compiledCampaign.endDateDisplay }}
          </span><br />
          <span class="text-lg font-bold">
            {{ compiledCampaign.durationSubtext }}
          </span>
        </div>

        <div class="col-2 py-0 flex align-items-center">
          <span class="text-lg font-bold">
            {{ compiledCampaign.bidDisplay }}
          </span>
        </div>

        <div class="col-2 py-0 flex align-items-center">
          <Chip
            :class="compiledCampaign.statusChipClass"
          >
            {{ compiledCampaign.statusChipValue }}
          </Chip>
        </div>

        <div class="col-3 py-0">
          <div class="analytics-column-container">
            <Skeleton
              v-if="impressionsAreLoading"
              class="h-3rem"
            />
            <Chart
              v-show="!impressionsAreLoading"
              class="h-3rem"
              type="line"
              :data="chartData"
              :options="chartOptions"
            />
            <Button
              v-if="isDropdownVisible"
              class="campaign-options-button"
              icon="pi pi-ellipsis-v"
              text
              plain
              aria-haspopup="true"
              aria-controls="campaign-row-menu"
              @click="(event) => this.$refs.campaignRowDropdown.toggle(event)"
            />
            <Menu
              id="campaign-row-menu"
              class="right-align-menu"
              ref="campaignRowDropdown"
              popup
              :model="dropdownItems"
            />
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<script>
import { DateTime } from 'luxon';
import { ROUTE_ADVERTISER_CAMPAIGN_DETAILS } from '@/router/routes';
import {
  CAMPAIGN_STATUS_ACTIVE,
  CAMPAIGN_STATUS_CREATED,
  CAMPAIGN_STATUS_PAUSED,
  CAMPAIGN_STATUS_PENDING,
} from '@/constants';
import { getCampaignStatusChip } from '@/utils/statuses';

export default {
  props: {
    campaign: {
      type: Object,
    },
    impressions: {
      type: Array,
    },
    impressionsAreLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /**
     * @deprecated - unused atm. bid column has taken the place of cost
     */
    revenueDisplay() {
      const totalImpressions = this.impressions.reduce((acc, { count }) => acc + count, 0);

      const revenue = this.campaign.bid_cap * (totalImpressions / 1000);

      return typeof revenue === 'number'
        ? revenue.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          style: 'currency',
          currency: 'USD',
        })
        : '$0';
    },
    compiledCampaign() {
      const { campaign } = this;

      const startDateDisplay = DateTime.fromISO(campaign.start_date)
        .toLocaleString(DateTime.DATE_SHORT);
      const endDateDisplay = DateTime.fromISO(campaign.end_date)
        .toLocaleString(DateTime.DATE_SHORT);

      const campaignHasStarted = DateTime.fromISO(campaign.start_date).diffNow('days').days < 0;
      let durationSubtext = '';

      if (campaignHasStarted) {
        const daysLeft = Math.max(
          Math.ceil(DateTime.fromISO(campaign.end_date).diffNow('days').days),
          0,
        );

        durationSubtext = `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;
      } else {
        const daysUntilStart = Math.ceil(DateTime.fromISO(campaign.start_date).diffNow('days').days);

        durationSubtext = `${daysUntilStart} day${daysUntilStart === 1 ? '' : 's'} until start`;
      }

      const bidDisplay = !Number.isNaN(Number(campaign.bid_cap))
        ? Number(campaign.bid_cap).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          style: 'currency',
          currency: 'USD',
        })
        : 'N/A';

      const statusChip = getCampaignStatusChip(campaign.status);

      return {
        ...campaign,
        startDateDisplay,
        endDateDisplay,
        durationSubtext,
        bidDisplay,
        statusChipClass: statusChip.class,
        statusChipValue: statusChip.value,
      };
    },
    chartData() {
      const documentStyle = getComputedStyle(document.documentElement);

      return {
        datasets: [
          {
            label: 'Impressions',
            data: this.impressions
              ? this.impressions.map(({
                count,
                timestamp,
              }) => ({
                x: timestamp,
                y: count,
              }))
              : [],
            fill: false,
            borderColor: documentStyle.getPropertyValue('--blue-400'),
            borderWidth: 3,
            pointRadius: 1,
          },
        ],
      };
    },
    dropdownItems() {
      return [
        {
          label: 'Edit',
          command: () => {
            this.$emit('editCampaign', this.campaign);
          },
          visible: (this.campaign.status === CAMPAIGN_STATUS_CREATED
            || this.campaign.status === CAMPAIGN_STATUS_PENDING),
        },
        {
          label: 'Pause',
          command: () => {
            this.$emit('pauseCampaign', this.campaign);
          },
          visible: () => this.campaign && this.campaign.status === CAMPAIGN_STATUS_ACTIVE,
        },
        {
          label: 'Resume',
          command: () => {
            this.$emit('resumeCampaign', this.campaign);
          },
          visible: () => this.campaign && this.campaign.status === CAMPAIGN_STATUS_PAUSED,
        },
        {
          label: 'Export Impressions',
          command: () => {
            this.$emit('exportImpressions', {
              campaign: this.campaign,
              impressions: this.impressions,
            });
          },
        },
      ];
    },
    isDropdownVisible() {
      return this.campaign
        && ([
          CAMPAIGN_STATUS_CREATED,
          CAMPAIGN_STATUS_PENDING,
          CAMPAIGN_STATUS_PAUSED,
          CAMPAIGN_STATUS_ACTIVE,
        ]).includes(this.campaign.status);
    },
  },
  data() {
    return {
      ROUTE_ADVERTISER_CAMPAIGN_DETAILS,
      chartOptions: {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
          legend: {
            display: false,
          },
        },
        elements: {
          line: {
            tension: 0.4,
          },
        },
        layout: {
          padding: 4,
        },
        scales: {
          x: {
            display: false,
          },
          y: {
            display: false,
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.bg-light-orange {
  background-color: #fef8f3;
}
.bg-light-green {
  background-color: #f4fdf7;
}
.bg-light-blue {
  background-color: #f5f9ff;
}

.analytics-column-container {
  padding-right: 40px;
  position: relative;
}
.campaign-options-button {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
}
</style>
